import React, { useEffect, useState } from "react";
// import { navbar } from "@material-tailwind/react";
import { Link } from "react-router-dom";
import {
  FaChevronDown,
  FaCalendarAlt,
  FaUserTie,
  FaInfoCircle,
  FaBalanceScale,
} from "react-icons/fa";
import { api } from "../../service/api.service";

const NAV_MENU = [
  {
    label: "Katha",
    items: [
      { label: "Daily Katha", to: "/videos", thumbKey: "dailyKatha" },
      { label: "Granth Katha", to: "/videos", thumbKey: "granthKatha" },
      { label: "Live Katha", to: "#", thumbKey: "liveKatha" },
    ],
  },
  {
    label: "Kirtan",
    items: [
      { label: "Kirtan Vivechan", to: "/videos", thumbKey: "kirtanVivechan" },
      { label: "Kirtan Juke Box", to: "/videos", thumbKey: "kirtanJukebox" },
    ],
  },
  {
    label: "Dhun",
    items: [{ label: "Swaminarayan Dhun", to: "/videos", thumbKey: "dhun" }],
  },
  {
    label: "Dhyan",
    items: [
      { label: "Dhyan Chintamani", to: "/videos", thumbKey: "dhyanChintamani" },
    ],
  },
  {
    label: "Reading",
    items: [{ label: "Books", to: "/books", image: "/assets/books.png" }],
  },
  {
    label: "Photos",
    items: [
      { label: "Events", to: "/events", image: "/assets/event.png" },
      { label: "Wallpaper", to: "/wallpaper", image: "/assets/tools/Wallpaper.svg" },
    ],
  },
  {
    label: "More",
    items: [
      { label: "Nirnay", to: "/nirnay", icon: FaBalanceScale },
      {
        label: "Mantra Lekhan",
        to: "/mantra-lekhan-signin",
        image: "/assets/tools/Mantralekhan Icon.svg",
      },
      { label: "Activities", to: "#", icon: FaCalendarAlt },
      { label: "Our Saint", to: "#", icon: FaUserTie },
      { label: "About", to: "#", icon: FaInfoCircle },
    ],
  },
];

function NavDropdownItem({ label, to, image, icon: Icon }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 px-3 py-2 mx-1 rounded-lg hover:bg-secondary transition-colors duration-200 group"
    >
      <span className="w-9 h-9 rounded-lg overflow-hidden border border-primary/30 bg-secondary flex items-center justify-center shrink-0">
        {image ? (
          <img src={image} alt="" className="w-full h-full object-cover" />
        ) : Icon ? (
          <Icon className="text-primary text-sm" />
        ) : (
          <span className="w-2 h-2 rounded-full bg-primary/40" />
        )}
      </span>
      <span className="text-sm text-gray-700 group-hover:text-primary transition-colors">
        {label}
      </span>
    </Link>
  );
}

function NavDropdown({ label, items, thumbnails }) {
  return (
    <div className="relative group">
      <button
        type="button"
        className="flex items-center gap-1 px-3 py-2 text-primary hover:text-hoverprimary transition ease-in-out duration-300"
      >
        {label}
        <FaChevronDown className="text-xs mt-[1px]" />
      </button>
      <div className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 absolute left-0 top-full pt-2 z-50">
        <div className="min-w-[230px] bg-white rounded-xl shadow-lg border border-slate-100 py-2">
          {items.map((item) => (
            <NavDropdownItem
              key={item.label}
              label={item.label}
              to={item.to}
              image={item.image || (item.thumbKey ? thumbnails[item.thumbKey] : undefined)}
              icon={item.icon}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileNavGroup({ label, items, isOpen, onToggle, thumbnails }) {
  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 text-primary font-medium"
      >
        {label}
        <FaChevronDown
          className={`text-xs transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="pl-3 flex flex-col">
          {items.map((item) => (
            <NavDropdownItem
              key={item.label}
              label={item.label}
              to={item.to}
              image={item.image || (item.thumbKey ? thumbnails[item.thumbKey] : undefined)}
              icon={item.icon}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [openMobileGroup, setOpenMobileGroup] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [thumbnails, setThumbnails] = useState({});

  const toggleMobileGroup = (label) => {
    setOpenMobileGroup((prev) => (prev === label ? null : label));
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchThumbnails = async () => {
      try {
        const [kathaRes, liveRes, playlistRes] = await Promise.all([
          fetch(`${api.defaults.baseURL}admin/all-katha-video-list`, {
            headers: { Accept: "application/json" },
          }),
          fetch(`${api.defaults.baseURL}admin/all-live-video-list`, {
            headers: { Accept: "application/json" },
          }),
          fetch(`${api.defaults.baseURL}admin/all-playlist`, {
            headers: { Accept: "application/json" },
          }),
        ]);

        const kathaJson = await kathaRes.json();
        const liveJson = await liveRes.json();
        const playlistJson = await playlistRes.json();

        const dailyKatha = (kathaJson?.data || []).find(
          (v) => v?.visibility !== false && v?.isDeleted !== true
        );
        const liveKatha = (liveJson?.data || []).find(
          (v) => v?.visibility !== false && v?.isDeleted !== true
        );
        const playlists = (playlistJson?.data || []).filter(
          (p) => p?.visibility !== false && p?.isDeleted !== true
        );
        const findPlaylist = (predicate) => playlists.find(predicate);
        const isCategory = (p, cat) => (p.category || "").trim().toLowerCase() === cat;

        const granthKatha = findPlaylist((p) => isCategory(p, "katha"));
        const kirtanJukebox = findPlaylist(
          (p) => isCategory(p, "kirtan") && /juke\s*box/i.test(p.name)
        );
        const kirtanVivechan = findPlaylist(
          (p) => isCategory(p, "kirtan") && !/juke\s*box/i.test(p.name)
        );
        const dhun = findPlaylist((p) => isCategory(p, "dhun"));
        const dhyanChintamani = findPlaylist((p) => /dhyan/i.test(p.name));

        setThumbnails({
          dailyKatha: dailyKatha?.thumbnailImages?.hqdefault,
          liveKatha: liveKatha?.thumbnailImages?.hqdefault,
          granthKatha: granthKatha?.thumbnailImages?.hqdefault,
          kirtanVivechan: kirtanVivechan?.thumbnailImages?.hqdefault,
          kirtanJukebox: kirtanJukebox?.thumbnailImages?.hqdefault,
          dhun: dhun?.thumbnailImages?.hqdefault,
          dhyanChintamani: dhyanChintamani?.thumbnailImages?.hqdefault,
        });
      } catch (err) {
        console.error("Error fetching nav thumbnails:", err);
      }
    };

    fetchThumbnails();
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 bg-lightred transition-shadow duration-300 ${
          isScrolled ? "shadow-md" : "header-section"
        }`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between pt-5 pb-5">
            <div className="flex space-x-4">
              <div>
                <Link to="/" className="flex items-center">
                  {/* <span className="font-bold">Brand</span> */}
                  <img src="/assets/logo.png" alt="logo" className="h-14" />
                </Link>
              </div>
            </div>
            <div className="hidden lg:flex items-center space-x-1 ">
              {/* <Link
                to="/daily-darshan"
                className="px-3 text-gray-700 hover:text-primary transition ease-in-out  duration-300 "
              >
                Daily Darshan
              </Link> */}
              {NAV_MENU.map((menu) => (
                <NavDropdown
                  key={menu.label}
                  label={menu.label}
                  items={menu.items}
                  thumbnails={thumbnails}
                />
              ))}
              <Link
                to="/contactus"
                className="text-white bg-primary hover:bg-hoverprimary transition-colors duration-300 ease px-4 py-3 rounded-3xl ml-2"
              >
                Contact Us
              </Link>
            </div>
            <div className="lg:hidden flex items-center">
              <button
                className="mobile-menu-button"
                onClick={() => setIsOpen(!isOpen)}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16m-7 6h7"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className={`mobile-menu ${isOpen ? "block" : "hidden"} lg:hidden`}>
          <div className="flex flex-col p-4">
            <Link
              to="/daily-darshan"
              className="px-3 text-gray-700 hover:text-primary transition ease-in-out  duration-300 mb-4"
            >
              Daily Darshan
            </Link>
            {NAV_MENU.map((menu) => (
              <MobileNavGroup
                key={menu.label}
                label={menu.label}
                items={menu.items}
                isOpen={openMobileGroup === menu.label}
                onToggle={() => toggleMobileGroup(menu.label)}
                thumbnails={thumbnails}
              />
            ))}
            <Link
              to="/contactus"
              className="text-white bg-primary hover:bg-hoverprimary transition-colors duration-300 ease px-4 py-3 rounded-3xl text-center mt-2"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </nav>
    </>
  );
}

export default Navbar;
